// VivaArtistTab — admin moderation for VIVA ARTIST label submissions.
// Lists candidatures, allows status transitions, notes edition and deletion,
// plus CSV export. Follows the visual conventions of HospitaliteTab.
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const STATUS_OPTS = [
  { value: "pending",     label: "En attente",   color: "#a89978" },
  { value: "in_review",   label: "En lecture",   color: "#7a6b50" },
  { value: "in_dialogue", label: "En dialogue",  color: "#d4af37" },
  { value: "accepted",    label: "Accepté",      color: "#2e7d32" },
  { value: "declined",    label: "Décliné",      color: "#7b1f2b" },
  { value: "archived",    label: "Archivé",      color: "#7a6b50" },
];

const DISCIPLINE_LABELS = {
  audio:     "Audio",
  video:     "Vidéo",
  image:     "Image",
  immersive: "Immersif",
  autre:     "Autre",
};

const STAGE_LABELS = {
  idea:           "Idée",
  writing:        "Écriture",
  preproduction:  "Préproduction",
  production:     "Production",
  postproduction: "Postproduction",
  distribution:   "Diffusion",
};

const SUPPORT_LABELS = {
  recording:            "Enregistrement",
  filming:              "Tournage",
  editing:              "Montage",
  mixing:               "Mixage",
  mastering:            "Mastering",
  direction_artistique: "Direction artistique",
  edition:              "Édition",
  diffusion:            "Diffusion",
  immersive_design:     "Immersif",
  other:                "Autre",
};

export default function VivaArtistTab() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/viva-artist/submissions");
      setItems(data.items || []);
      setStats(data.stats || {});
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (
    filter === "all" ? items : items.filter((i) => i.status === filter)
  ), [items, filter]);

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/viva-artist/submissions/${id}`, { status });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette candidature ?")) return;
    await api.delete(`/admin/viva-artist/submissions/${id}`);
    load();
  };

  const exportCsv = () => {
    const rows = [
      ["date", "prénom", "nom", "nom_artiste", "email", "téléphone", "pays",
       "discipline", "médium", "projet", "url", "stade", "accompagnement",
       "newsletter", "statut", "notes admin", "PDF joint"],
      ...items.map((it) => [
        (it.created_at || "").slice(0, 10),
        it.first_name, it.last_name, it.artist_name || "",
        it.email, it.phone || "", it.country || "",
        DISCIPLINE_LABELS[it.discipline] || it.discipline,
        it.medium || "",
        it.project_title,
        it.project_url || "",
        STAGE_LABELS[it.stage_of_project] || it.stage_of_project,
        (it.desired_support || []).map((s) => SUPPORT_LABELS[s] || s).join(" · "),
        it.consent_newsletter ? "oui" : "non",
        STATUS_OPTS.find((o) => o.value === it.status)?.label || it.status,
        it.notes || "",
        it.portfolio_pdf_url || "",
      ]),
    ];
    const csv = "\ufeff" + rows.map((r) =>
      r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")
    ).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `viva-artist-candidatures-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  return (
    <div data-testid="viva-artist-tab" className="space-y-6">
      {/* Header line */}
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl" style={{ color: "var(--ink)" }}>
            VIVA ARTIST · Candidatures
          </h2>
          <p className="font-body text-sm mt-1" style={{ color: "var(--muted)" }}>
            Modération des projets confiés au label de production.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load}
                  data-testid="viva-admin-refresh"
                  className="btn btn-outline"
                  style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem" }}>
            ↻ Rafraîchir
          </button>
          <button onClick={exportCsv}
                  data-testid="viva-admin-export-csv"
                  className="btn btn-outline"
                  style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem" }}>
            ↓ Export CSV
          </button>
        </div>
      </div>

      {/* Stats + status filter */}
      <div className="flex flex-wrap gap-2 items-center">
        <StatPill
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label="TOUS"
          value={stats.total || 0}
          testid="viva-filter-all"
        />
        {STATUS_OPTS.map((o) => (
          <StatPill
            key={o.value}
            active={filter === o.value}
            onClick={() => setFilter(o.value)}
            label={o.label.toUpperCase()}
            value={stats[o.value] || 0}
            color={o.color}
            testid={`viva-filter-${o.value}`}
          />
        ))}
      </div>

      {/* Table */}
      {loading ? (
        <div className="py-12 text-center font-body" style={{ color: "var(--muted)" }}>
          Chargement…
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center font-body" style={{ color: "var(--muted)" }}>
          {filter === "all"
            ? "Aucune candidature pour l'instant. Le silence est aussi une réponse."
            : "Aucune candidature dans cet état."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="viva-admin-table">
            <thead style={{ background: "var(--parchment-2)" }}>
              <tr className="font-caps text-[0.62rem]"
                  style={{ letterSpacing: "0.12em", color: "var(--ink-2)" }}>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Artiste</th>
                <th className="text-left p-3">Projet</th>
                <th className="text-left p-3">Discipline / Stade</th>
                <th className="text-left p-3">Statut</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <SubmissionRow
                  key={it.id}
                  item={it}
                  expanded={expanded === it.id}
                  onToggle={() => setExpanded(expanded === it.id ? null : it.id)}
                  onStatusChange={(s) => updateStatus(it.id, s)}
                  onDelete={() => remove(it.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================================================
function StatPill({ active, onClick, label, value, color = "#a89978", testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="font-caps text-[0.62rem]"
      style={{
        letterSpacing: "0.14em",
        padding: "0.45rem 0.9rem",
        border: `1px solid ${active ? color : "var(--border)"}`,
        background: active ? color : "transparent",
        color: active ? "white" : "var(--ink-2)",
        cursor: "pointer",
        borderRadius: 0,
      }}
    >
      {label} · <strong>{value}</strong>
    </button>
  );
}

function StatusBadge({ status }) {
  const opt = STATUS_OPTS.find((o) => o.value === status) || STATUS_OPTS[0];
  return (
    <span
      className="font-caps text-[0.62rem]"
      style={{
        padding: "0.25rem 0.55rem",
        letterSpacing: "0.14em",
        background: opt.color,
        color: "white",
        display: "inline-block",
      }}
    >
      {opt.label}
    </span>
  );
}

function SubmissionRow({ item, expanded, onToggle, onStatusChange, onDelete }) {
  return (
    <>
      <tr
        className="border-t cursor-pointer hover:bg-[var(--parchment)]"
        style={{ borderColor: "var(--border)" }}
        onClick={onToggle}
        data-testid={`viva-row-${item.id}`}
      >
        <td className="p-3 font-mono text-xs align-top" style={{ color: "var(--muted)" }}>
          {(item.created_at || "").slice(0, 10)}
        </td>
        <td className="p-3 align-top">
          <div className="font-display">
            {item.artist_name || `${item.first_name} ${item.last_name}`}
          </div>
          {item.artist_name && (
            <div className="text-xs italic" style={{ color: "var(--muted)" }}>
              {item.first_name} {item.last_name}
            </div>
          )}
          <div className="text-xs mt-0.5" style={{ color: "var(--muted)" }}>
            {item.email}
            {item.phone ? ` · ${item.phone}` : ""}
            {item.country ? ` · ${item.country}` : ""}
          </div>
        </td>
        <td className="p-3 align-top">
          <div className="font-body font-medium flex items-center gap-2">
            {item.project_title}
            {item.portfolio_pdf_url && (
              <span
                title="Dossier PDF joint"
                data-testid={`viva-has-pdf-${item.id}`}
                style={{
                  fontSize: "0.62rem",
                  padding: "0.15rem 0.4rem",
                  background: "var(--gold-dark, #a68b3d)",
                  color: "white",
                  letterSpacing: "0.1em",
                }}
              >
                PDF
              </span>
            )}
          </div>
          {item.project_url && (
            <a
              href={item.project_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-xs underline"
              style={{ color: "var(--cinnabar)" }}
            >
              ↗ Portfolio / extrait
            </a>
          )}
        </td>
        <td className="p-3 align-top font-body text-xs" style={{ color: "var(--muted)" }}>
          <div>{DISCIPLINE_LABELS[item.discipline] || item.discipline}</div>
          {item.medium && <div className="italic mt-0.5">{item.medium}</div>}
          <div className="mt-1" style={{ color: "var(--gold-dark, #a68b3d)" }}>
            {STAGE_LABELS[item.stage_of_project] || item.stage_of_project}
          </div>
        </td>
        <td className="p-3 align-top" onClick={(e) => e.stopPropagation()}>
          <select
            value={item.status}
            onChange={(e) => onStatusChange(e.target.value)}
            data-testid={`viva-status-${item.id}`}
            className="form-input"
            style={{ padding: "0.35rem 0.5rem", fontSize: "0.78rem" }}
          >
            {STATUS_OPTS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <div className="mt-1.5"><StatusBadge status={item.status} /></div>
        </td>
        <td className="p-3 text-right align-top" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={onToggle}
              data-testid={`viva-expand-${item.id}`}
              className="font-caps text-[0.62rem]"
              style={{
                color: "var(--ink-2)",
                letterSpacing: "0.16em",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              {expanded ? "▲ Réduire" : "▼ Détail"}
            </button>
            <button
              onClick={onDelete}
              data-testid={`viva-delete-${item.id}`}
              className="font-caps text-[0.62rem]"
              style={{
                color: "var(--cinnabar)",
                letterSpacing: "0.16em",
                background: "none",
                border: "none",
                cursor: "pointer",
              }}
            >
              Supprimer
            </button>
          </div>
        </td>
      </tr>
      {expanded && (
        <tr
          className="border-t"
          style={{ borderColor: "var(--border)", background: "var(--parchment)" }}
          data-testid={`viva-detail-${item.id}`}
        >
          <td colSpan={6} className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="md:col-span-2 space-y-4">
                <DetailSection label="Présentation du projet">
                  <p
                    className="font-body text-sm whitespace-pre-wrap leading-relaxed"
                    style={{ color: "var(--ink)" }}
                  >
                    {item.project_pitch}
                  </p>
                </DetailSection>
                {item.notes && (
                  <DetailSection label="Autres notes de l'artiste">
                    <p
                      className="font-body text-sm whitespace-pre-wrap"
                      style={{ color: "var(--ink)" }}
                    >
                      {item.notes}
                    </p>
                  </DetailSection>
                )}
              </div>
              <div className="space-y-4">
                <DetailSection label="Accompagnement souhaité">
                  {item.desired_support?.length ? (
                    <ul
                      className="font-body text-sm space-y-1"
                      style={{ color: "var(--ink)" }}
                    >
                      {item.desired_support.map((s) => (
                        <li key={s}>· {SUPPORT_LABELS[s] || s}</li>
                      ))}
                    </ul>
                  ) : (
                    <em
                      className="font-body text-xs"
                      style={{ color: "var(--muted)" }}
                    >
                      Non précisé
                    </em>
                  )}
                </DetailSection>
                <DetailSection label="Newsletter">
                  <span
                    className="font-body text-sm"
                    style={{ color: item.consent_newsletter ? "#2e7d32" : "var(--muted)" }}
                  >
                    {item.consent_newsletter ? "✓ Consentement" : "— Non demandé"}
                  </span>
                </DetailSection>
                {item.portfolio_pdf_url && (
                  <DetailSection label="Dossier PDF joint">
                    <a
                      href={item.portfolio_pdf_url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-body text-sm underline flex items-center gap-1"
                      style={{ color: "var(--cinnabar)" }}
                      data-testid={`viva-portfolio-link-${item.id}`}
                    >
                      ↓ {item.portfolio_pdf_name || "Dossier.pdf"}
                      {item.portfolio_pdf_size && (
                        <span style={{ color: "var(--muted)" }}>
                          {" "}({(item.portfolio_pdf_size / 1024 / 1024).toFixed(2)} Mo)
                        </span>
                      )}
                    </a>
                  </DetailSection>
                )}
                <div className="pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                  <a
                    href={`mailto:${item.email}?subject=${encodeURIComponent(
                      "VIVA ARTIST · " + item.project_title,
                    )}`}
                    className="btn btn-outline"
                    style={{ fontSize: "0.7rem", padding: "0.5rem 0.9rem" }}
                    data-testid={`viva-mailto-${item.id}`}
                  >
                    ✉ Répondre par courriel
                  </a>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DetailSection({ label, children }) {
  return (
    <div>
      <div
        className="font-caps text-[0.62rem] mb-1.5"
        style={{ color: "var(--gold-dark, #a68b3d)", letterSpacing: "0.16em" }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}
